<template>
  <section class="settings-section">
    <div class="container px-3">
      <div class="row ">
        <!-- Sidebar -->
        <aside class="col-lg-3 px-lg-0">
          <div class="sidebar-wrapper">

            <!-- Navigation Menu -->
            <nav class="settings-nav">
              <!-- Admin Menu -->
              <div v-if="isAdmin" class="nav-section">
                <h6 class="nav-title">ADMINISTRACIÓN</h6>
                <a href="/admin/productos" class="nav-link">
                  <i class="fas fa-box"></i>
                  <span>Productos</span>
                </a>
                <a href="/admin/actividad" class="nav-link">
                  <i class="fas fa-chart-line"></i>
                  <span>Actividad</span>
                </a>
                <a href="/admin/pedidos" class="nav-link">
                  <i class="fas fa-shopping-cart"></i>
                  <span>Pedidos</span>
                </a>
              </div>

              <!-- User Menu -->
              <div class="nav-section">
                <h6 class="nav-title">MI CUENTA</h6>
                <a 
                  href="#orders" 
                  @click.prevent="setActiveSection('orders')"
                  :class="['nav-link', { active: activeSection === 'orders' }]"
                >
                  <i class="fas fa-shopping-bag"></i>
                  <span>Mis Pedidos</span>
                </a>
                <a 
                  href="#settings" 
                  @click.prevent="setActiveSection('settings')"
                  :class="['nav-link', { active: activeSection === 'settings' }]"
                >
                  <i class="fas fa-cog"></i>
                  <span>Configuración</span>
                </a>
              </div>
            </nav>

            <!-- Logout Button -->
            <button @click="handleLogout" class="logout-btn fs-6 mt-5">
              <i class="fas fa-sign-out-alt"></i>
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </aside>

        <!-- Main Content -->
        <main class="col-lg-9">
          <!-- Settings Section -->
          <div v-if="activeSection === 'settings'" class="content-section">
            <h2 class="section-title">Configuración de cuenta</h2>
            <div class="card d-flex">
              <div class="card-body px-0">
                <!-- Toggle Buttons -->
                <div class="mb-4 d-flex gap-3">
                  <button 
                    class="btn"
                    :class="showPasswordForm ? 'btn-outline-primary' : 'btn-primary'"
                    @click="showPasswordForm = false"
                  >
                    <i class="fas fa-user me-2"></i>
                    Datos Personales
                  </button>
                  <button 
                    class="btn"
                    :class="showPasswordForm ? 'btn-primary' : 'btn-outline-primary'"
                    @click="showPasswordForm = true"
                  >
                    <i class="fas fa-key me-2"></i>
                    Cambiar Contraseña
                  </button>
                </div>

                <!-- Personal Data Form -->
                <form v-if="!showPasswordForm" @submit.prevent="handleSubmit">
                  <div class="row mx-0">
                    <div class="col-md-6 px-0 pe-lg-2">
                      <div class="form-group">
                        <label>Nombre</label>
                        <input 
                          v-model="formData.first_name" 
                          type="text" 
                          class="form-control"
                          placeholder="Tu nombre"
                        >
                      </div>
                    </div>

                    <div class="col-md-6 ps-md-2 pe-0">
                      <div class="form-group">
                        <label>Apellido</label>
                        <input 
                          v-model="formData.last_name" 
                          type="text" 
                          class="form-control"
                          placeholder="Tu apellido"
                        >
                      </div>
                    </div>
                    
                    <div class="col-12 px-0">
                      <div class="form-group">
                        <label>Email</label>
                        <div class="d-flex gap-2">
                          <input 
                            v-model="formData.email" 
                            type="email" 
                            class="form-control"
                            placeholder="tu@email.com"
                            :disabled="isChangingEmail"
                          >
                          <button 
                            v-if="formData.email !== originalEmail && !isChangingEmail"
                            type="button" 
                            class="btn btn-outline-primary"
                            @click="requestEmailChangeCode"
                          >
                            <i class="fas fa-envelope me-2"></i>
                            Verificar
                          </button>
                        </div>
                      </div>

                      <!-- Email verification code input -->
                      <div class="form-group" v-if="isChangingEmail">
                        <label>Código de verificación</label>
                        <div class="input-group">
                          <input 
                            v-model="emailVerificationCode"
                            type="text"
                            class="form-control"
                            placeholder="Ingresa el código de 6 dígitos"
                            maxlength="6"
                          >
                          <button 
                            type="button" 
                            class="btn btn-primary"
                            @click="verifyAndUpdateEmail"
                            :disabled="emailVerificationCode.length !== 6"
                          >
                            <i class="fas fa-check me-2"></i>
                            Verificar
                          </button>
                          <button 
                            type="button" 
                            class="btn btn-outline-secondary"
                            @click="cancelEmailChange"
                          >
                            <i class="fas fa-times"></i>
                          </button>
                        </div>
                        <small class="text-muted">Hemos enviado un código de verificación al nuevo email.</small>
                      </div>
                    </div>
                  </div>

                  <!-- Email verification -->
                  <div v-if="isChangingEmail" class="alert alert-info">
                    <i class="fas fa-info-circle me-2"></i>
                    Para cambiar tu email, ingresa el código de verificación enviado a <strong>{{ formData.email }}</strong>
                  </div>

                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary" :disabled="isLoading || isChangingEmail">
                      <i class="fas fa-save me-2"></i>
                      {{ isLoading ? 'Guardando...' : 'Guardar cambios' }}
                    </button>
                  </div>
                </form>

                <!-- Password Change Form -->
                <form v-else @submit.prevent="handlePasswordChange">
                  <div class="row mx-0">
                    <div class="col-12 px-0"> 
                      <div class="form-group">
                        <label>Contraseña Actual</label>
                        <div class="input-group">
                          <input 
                            :type="showCurrentPassword ? 'text' : 'password'" 
                            v-model="passwordForm.currentPassword" 
                            class="form-control"
                            placeholder="Ingresa tu contraseña actual"
                          >
                          <button 
                            type="button" 
                            class="btn btn-outline-secondary"
                            @click="showCurrentPassword = !showCurrentPassword"
                          >
                            <i :class="showCurrentPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div class="col-md-6 px-0 pe-md-2"> <!-- Ajustado padding -->
                      <div class="form-group">
                        <label>Nueva Contraseña</label>
                        <div class="input-group">
                          <input 
                            :type="showNewPassword ? 'text' : 'password'" 
                            v-model="passwordForm.newPassword" 
                            class="form-control"
                            placeholder="Ingresa tu nueva contraseña"
                          >
                          <button 
                            type="button" 
                            class="btn btn-outline-secondary"
                            @click="showNewPassword = !showNewPassword"
                          >
                            <i :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div class="col-md-6 ps-md-2 pe-0"> <!-- Ajustado padding -->
                      <div class="form-group">
                        <label>Confirmar Nueva Contraseña</label>
                        <div class="input-group">
                          <input 
                            :type="showConfirmPassword ? 'text' : 'password'" 
                            v-model="passwordForm.confirmPassword" 
                            class="form-control"
                            placeholder="Confirma tu nueva contraseña"
                          >
                          <button 
                            type="button" 
                            class="btn btn-outline-secondary"
                            @click="showConfirmPassword = !showConfirmPassword"
                          >
                            <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary" :disabled="isChangingPassword">
                      <i class="fas fa-key me-2"></i>
                      {{ isChangingPassword ? 'Cambiando...' : 'Cambiar Contraseña' }}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <!-- Orders Section -->
          <div v-if="activeSection === 'orders'" class="content-section">
            <h2 class="section-title">Mis Pedidos</h2>
            <UserOrdersList />  <!-- Eliminar props ya que el componente manejará su propio estado -->
          </div>
        </main>
      </div>
    </div>

    <!-- Password Verification Modal -->
    <div class="modal-overlay" v-if="showPasswordModal">
      <div class="password-modal">
        <div class="modal-header">
          <h5>Verificación de seguridad</h5>
          <button type="button" class="btn-close" @click="cancelPasswordModal"></button>
        </div>
        <div class="modal-body">
          <p>Por favor ingresa tu contraseña para continuar con el cambio de email.</p>
          <div class="form-group">
            <div class="input-group">
              <input 
                :type="showModalPassword ? 'text' : 'password'" 
                v-model="emailVerificationPassword"
                class="form-control" 
                placeholder="Tu contraseña actual" 
                @keyup.enter="submitPasswordModal"
              >
              <button 
                type="button" 
                class="btn btn-outline-secondary"
                @click="showModalPassword = !showModalPassword"
              >
                <i :class="showModalPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>
          <div v-if="passwordModalError" class="alert alert-danger mt-2">
            {{ passwordModalError }}
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-outline-secondary" @click="cancelPasswordModal">
            Cancelar
          </button>
          <button 
            class="btn btn-primary" 
            :disabled="!emailVerificationPassword || passwordModalLoading"
            @click="submitPasswordModal"
          >
            <i class="fas fa-spinner fa-spin me-2" v-if="passwordModalLoading"></i>
            {{ passwordModalLoading ? 'Verificando...' : 'Continuar' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { getUserInfo, updateUserProfile, changePassword, requestVerificationCode, updateEmailWithVerification } from '@/services/auth'
import { useVuelidate } from '@vuelidate/core'
import { email, minLength, helpers } from '@vuelidate/validators'
import UserOrdersList from '@/components/UserOrdersList.vue'

const router = useRouter()
const toast = useToast()

const isAdmin = ref(localStorage.getItem('isAdmin') === 'true')
const activeSection = ref('orders')
const isLoading = ref(false)

// Add missing email verification related variables
const originalEmail = ref('')
const isChangingEmail = ref(false)
const emailVerificationCode = ref('')
const newEmailAddress = ref('')
const emailVerificationPassword = ref('')

// Estado del formulario usando reactive para validaciones
const formData = reactive({
  first_name: '',
  last_name: '',
  email: '',
  new_password: '',
  confirm_password: ''
})

const rules = {
  first_name: { 
    minLength: helpers.withMessage('El nombre debe tener al menos 2 caracteres', minLength(2))
  },
  last_name: { 
    minLength: helpers.withMessage('El apellido debe tener al menos 2 caracteres', minLength(2))
  },
  email: {
    email: helpers.withMessage('Ingrese un email válido', email)
  },
}

const v$ = useVuelidate(rules, formData)

const setActiveSection = (section) => {
  activeSection.value = section
}

const handleSubmit = async () => {
  try {
    const isFormValid = await v$.value.$validate()
    if (!isFormValid) {
      const firstError = Object.values(v$.value.$errors)[0]
      if (firstError) {
        toast.error(firstError.$message)
      }
      return
    }

    // Check if email has changed - if yes, we need verification
    if (formData.email !== originalEmail.value) {
      // Trigger email verification workflow
      requestEmailChangeCode()
      return
    }

    isLoading.value = true

    // Create update object - only include fields that we want to update
    const updateData = {
      first_name: formData.first_name,
      last_name: formData.last_name
      // Email is handled separately with verification
    }

    await updateUserProfile(updateData)
    toast.success('Perfil actualizado correctamente')
    
  } catch (error) {
    console.error('Error updating profile:', error)
    toast.error(error.error || 'Error al actualizar el perfil')
  } finally {
    isLoading.value = false
  }
}

// Add modal state variables
const showPasswordModal = ref(false)
const showModalPassword = ref(false) 
const passwordModalLoading = ref(false)
const passwordModalError = ref('')
const pendingEmailChange = ref(false)

// Request verification code for email change
const requestEmailChangeCode = async () => {
  // Store the new email address for verification
  newEmailAddress.value = formData.email
  
  // Show password modal instead of prompt
  showPasswordModal.value = true
  pendingEmailChange.value = true
}

// Submit password modal
const submitPasswordModal = async () => {
  if (!emailVerificationPassword.value) {
    passwordModalError.value = 'Por favor ingresa tu contraseña'
    return
  }

  passwordModalLoading.value = true
  passwordModalError.value = ''

  try {
    if (pendingEmailChange.value) {
      // Request verification code 
      await requestVerificationCode(
        'EMAIL_CHANGE', 
        formData.email,  // New email
        emailVerificationPassword.value  // Current password
      )

      // Close modal and show verification form
      showPasswordModal.value = false
      isChangingEmail.value = true
      toast.info('Código de verificación enviado al nuevo email')
    }
  } catch (error) {
    console.error('Error requesting code:', error)
    passwordModalError.value = error.error || 'Contraseña incorrecta o error al solicitar código'
  } finally {
    passwordModalLoading.value = false
    pendingEmailChange.value = false
  }
}

// Cancel password modal
const cancelPasswordModal = () => {
  showPasswordModal.value = false
  emailVerificationPassword.value = ''
  passwordModalError.value = ''
  pendingEmailChange.value = false
  
  // Revert email change if cancelling
  if (pendingEmailChange.value) {
    formData.email = originalEmail.value
  }
}

// Verify and update email
const verifyAndUpdateEmail = async () => {
  if (emailVerificationCode.value.length !== 6) {
    toast.error('El código debe tener 6 dígitos')
    return
  }

  try {
    isLoading.value = true
    await updateEmailWithVerification(newEmailAddress.value, emailVerificationCode.value)
    
    // Update original email after successful change
    originalEmail.value = newEmailAddress.value
    
    // Reset verification state
    isChangingEmail.value = false
    emailVerificationCode.value = ''
    
    toast.success('Email actualizado correctamente')
  } catch (error) {
    toast.error(error.error || 'Código inválido o expirado')
  } finally {
    isLoading.value = false
  }
}

// Cancel email change
const cancelEmailChange = () => {
  // Restore original email
  formData.email = originalEmail.value
  isChangingEmail.value = false
  emailVerificationCode.value = ''
  newEmailAddress.value = ''
}

const handleLogout = () => {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('isAdmin') 
  localStorage.removeItem('cart')
  localStorage.removeItem('userOrders')
  localStorage.removeItem('adminOrders')
  window.location.reload()
}

const showPasswordForm = ref(false)
const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const isChangingPassword = ref(false)

const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const handlePasswordChange = async () => {
  // Validaciones
  if (!passwordForm.currentPassword) {
    toast.error('Ingresa tu contraseña actual')
    return
  }
  
  if (!passwordForm.newPassword) {
    toast.error('Ingresa la nueva contraseña')
    return
  }

  if (passwordForm.newPassword.length < 6) {
    toast.error('La nueva contraseña debe tener al menos 6 caracteres')
    return
  }

  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    toast.error('Las contraseñas no coinciden')
    return
  }

  try {
    isChangingPassword.value = true
    
    await changePassword({
      old_password: passwordForm.currentPassword,
      new_password: passwordForm.newPassword
    })

    toast.success('Contraseña actualizada correctamente')
    
    // Limpiar formulario
    passwordForm.currentPassword = ''
    passwordForm.newPassword = ''
    passwordForm.confirmPassword = ''
    showPasswordForm.value = false

  } catch (error) {
    console.error('Error changing password:', error)
    const errorMessage = error.response?.data?.error || 'Error al cambiar la contraseña'
    toast.error(errorMessage)
  } finally {
    isChangingPassword.value = false
  }
}

onMounted(async () => {
  // Obtener la sección de la URL si existe
  const urlParams = new URLSearchParams(window.location.search);
  const section = urlParams.get('section');
  if (section) {
    activeSection.value = section;
  }

  try {
    // Solo cargar información del usuario
    const user = await getUserInfo()
    formData.first_name = user.first_name || ''
    formData.last_name = user.last_name || ''
    formData.email = user.email || ''
    
    // Store original email for comparison
    originalEmail.value = user.email || ''
    
  } catch (error) {
    console.error('Error loading user info:', error)
    if (error.response?.status === 401) {
      router.push('/login')
    } else {
      toast.error('Error al cargar la información')
    }
  }
})

</script>

<style scoped>
.settings-section {
  padding: 2rem 0;
  min-height: 100vh;
  background: var(--black-color);
}

.sidebar-wrapper {
  background: var(--black-color-3);
  border-radius: 1rem;
  padding: 1.5rem;
}

.col-md-6, .col-12 {
    padding-left: initial;
}

.user-profile {
  text-align: center;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.avatar-wrapper {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

.user-avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
}

.change-avatar-btn {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--primary-color);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.user-name {
  color: var(--white-color);
  margin-top: 1rem;
}

.user-role {
  color: var(--white-color-2);
  font-size: 0.875rem;
}

.nav-section {
  margin-bottom: 1.5rem;
}

.nav-title {
  color: var(--white-color-2);
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  padding-left: 0.5rem;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: var(--white-color);
  text-decoration: none;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  margin-bottom: 0.25rem;
}

.nav-link i {
  margin-right: 0.75rem;
  width: 20px;
  text-align: center;
}

.nav-link:hover,
.nav-link.active {
  background: var(--primary-color);
  color: var(--black-color);
}

.logout-btn {
  width: 100%;
  padding: 0.75rem;
  background: rgba(255, 59, 48, 0.1);
  color: #ff3b30;
  border: none;
  border-radius: 0.5rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background: rgba(255, 59, 48, 0.2);
}

.content-section {
  background: var(--black-color-3);
  border-radius: 1rem;
  padding: 2rem;
}

.section-title {
  color: var(--white-color);
  margin-bottom: 1.5rem;
}

.card {
  background: var(--black-color-3);
  border: none;
  border-radius: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  color: var(--white-color);
  margin-bottom: 0.5rem;
  display: block;
}

.form-control {
  background: var(--black-color-2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white-color);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.form-control:focus {
  background: var(--black-color-2);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: none;
}

.form-actions {
  margin-top: 2rem;
  display: flex;
  justify-content: flex-end;
}

.input-group .btn-outline-secondary {
  border-color: rgba(255, 255, 255, 0.1);
  color: var(--white-color-2);
}

.input-group .btn-outline-secondary:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--white-color);
}

.input-group .form-control {
  border-right: none;
}

.input-group .btn {
  border-left: none;
}

.orders-skeleton {
  min-height: 200px;
}

.skeleton-order {
  height: 200px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
}

.order-card {
  background: var(--black-color-2);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.order-header {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.order-id {
  font-family: monospace;
  color: var(--white-color-2);
}

.order-content {
  padding: 1rem;
}

.product-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 0.5rem;
  transition: transform 0.2s ease;
}

.product-item:hover {
  transform: translateX(5px);
}

.product-thumbnail {
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.25rem;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.info-row i {
  width: 16px;
  text-align: center;
}

.total-label {
  color: var(--white-color-2);
}

.total-amount {
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--primary-color);
}

@media (max-width: 991px) {
  .sidebar-wrapper {
    margin-bottom: 2rem;
    position: static;
  }
}

.alert {
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.alert-info {
  background-color: rgba(0, 123, 255, 0.1);
  border: 1px solid rgba(0, 123, 255, 0.2);
  color: #0dcaf0;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.password-modal {
  width: 95%;
  max-width: 420px;
  background-color: var(--black-color-3);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: modal-appear 0.3s ease-out;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header h5 {
  color: var(--white-color);
  margin: 0;
  font-weight: 600;
}

.modal-body {
  padding: 1rem;
}

.modal-body p {
  color: var(--white-color-2);
  margin-bottom: 1rem;
}

.modal-footer {
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.alert-danger {
  background-color: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.2);
  color: #fb6b67;
  border-radius: 8px;
}
</style>